import { bunja } from 'bunja';
import { atom } from 'jotai';

import { FundingTypeTimeMapper } from '@flipster/apis/v2/snapshot/constants';
import { ceilingsDiffer, getEffectiveIsolatedMax } from '@flipster/features/src/shared/leverage/isolated-cap';
import { positionBracketBunja } from '@flipster/features/src/shared/position-bracket/state';
import { BigNumber } from '@flipster/utils/num';

import { perpetualSymbolSocketBunja, tradeSymbolBunja } from '@web/app/[locale]/(web)/trade/_misc/symbol';

export interface FundingInterval {
  value: string;
  /** value 아래 보조(sub)로 붙는 타임존 표기 (예: "(UTC)"). Realtime 등 타임존이 무의미하면 없음. */
  sub?: string;
}

/**
 * Derive the daily UTC funding times from the funding cycle count.
 * e.g. 8h funding (cycle 3) -> { value: "00:00:00, 08:00:00, 16:00:00", sub: "(UTC)" }.
 * 타임존 표기는 value가 아니라 sub로 분리해 UI에서 보조 텍스트로 렌더한다.
 * For high-frequency cycles (hourly / realtime) the explicit list is impractical,
 * so fall back to an interval description.
 */
export const getFundingInterval = (fundingType: string | undefined): FundingInterval | undefined => {
  if (!fundingType) return undefined;
  const cycle = FundingTypeTimeMapper[fundingType as keyof typeof FundingTypeTimeMapper];
  if (typeof cycle !== 'number' || cycle <= 0) return undefined;

  const stepHours = 24 / cycle;
  if (!Number.isInteger(stepHours) || cycle > 6) {
    // hourly (24) or realtime (1440): listing every time is impractical
    return stepHours >= 1 ? { value: `Every ${stepHours}h`, sub: '(UTC)' } : { value: 'Realtime' };
  }

  const times = Array.from({ length: cycle }, (_, i) => `${String(i * stepHours).padStart(2, '0')}:00:00`);
  return { value: times.join(', '), sub: '(UTC)' };
};

/**
 * Trading parameter 패널의 데이터 레이어.
 * 관련 bunja(심볼 스펙 / pswap 소켓)를 결합해 각 파라미터 필드 값을 atom으로 파생한다.
 * UI(index.tsx)는 이 atom들을 consume만 하고 표시/포맷팅만 담당한다.
 */
export const tradingParameterBunja = bunja(() => {
  const { symbolInfoAtom, isSpotAtom, positionUnitAtom, quoteCurrencyAtom, fundingTypeAtom } =
    bunja.use(tradeSymbolBunja);
  const { getTickerAtom } = bunja.use(perpetualSymbolSocketBunja);
  const { positionBracketAtom } = bunja.use(positionBracketBunja);

  const fundingRateAtom = atom((get) => get(getTickerAtom)?.fundingRate);
  const fundingTimeAtom = atom((get) => get(getTickerAtom)?.fundingTime);
  const fundingIntervalAtom = atom((get) => getFundingInterval(get(fundingTypeAtom)));
  const fundingIntervalTextAtom = atom((get) => get(fundingIntervalAtom)?.value);
  const fundingIntervalTimezoneAtom = atom((get) => get(fundingIntervalAtom)?.sub);

  const tickSizeAtom = atom((get) => get(symbolInfoAtom)?.tickSize);
  const minTradeAmountAtom = atom((get) => get(symbolInfoAtom)?.unitOrderQty);
  const tradeQtyPrecisionAtom = atom((get) => get(symbolInfoAtom)?.tradeQtyPrecision);
  const maxNotionalOrderAmountAtom = atom((get) => get(symbolInfoAtom)?.notionalMaxOrderAmount);
  const maxNotionalPositionSizeAtom = atom((get) => get(symbolInfoAtom)?.notionalMaxPosition);
  const minNotionalOrderAmountAtom = atom((get) => get(symbolInfoAtom)?.notionalMinOrderAmount);
  const priceProtectionThresholdAtom = atom((get) => get(symbolInfoAtom)?.defaultMaxSlippage);
  // Cross max leverage = 현재 position bracket의 최대 maxInitLeverage (ERD-240 §5).
  // positionBracketAtom이 WS ?? specs로 해석되므로(§10) off-hours bracket 스위칭 시 자동 반영된다.
  const crossMaxAtom = atom((get) => {
    const bracket = get(positionBracketAtom);
    if (!bracket?.entries.length) return undefined;
    return Math.max(...bracket.entries.map((entry) => entry.maxInitLeverage));
  });

  const effectiveIsolatedMaxAtom = atom((get) => {
    const crossMax = get(crossMaxAtom);
    if (crossMax == null) return undefined;
    return getEffectiveIsolatedMax(crossMax, get(symbolInfoAtom)?.maxInitLeverageIsolatedMargin);
  });

  const ceilingsDifferAtom = atom((get) => {
    const crossMax = get(crossMaxAtom);
    const effectiveIsolatedMax = get(effectiveIsolatedMaxAtom);
    if (crossMax == null || effectiveIsolatedMax == null) return false;
    return ceilingsDiffer(crossMax, effectiveIsolatedMax);
  });

  // Max open interest = min(long, short reduce-only trigger OI) (ERD-240 §5)
  const maxOpenInterestAtom = atom((get) => {
    const info = get(symbolInfoAtom);
    const long = info?.longPositionReduceOnlyTriggerOiInUsdt;
    const short = info?.shortPositionReduceOnlyTriggerOiInUsdt;
    if (long != null && short != null) return BigNumber.min(long, short);
    return long ?? short;
  });

  return {
    isSpotAtom,
    positionUnitAtom,
    quoteCurrencyAtom,
    fundingRateAtom,
    fundingTimeAtom,
    fundingIntervalTextAtom,
    fundingIntervalTimezoneAtom,
    tickSizeAtom,
    minTradeAmountAtom,
    tradeQtyPrecisionAtom,
    maxNotionalOrderAmountAtom,
    maxNotionalPositionSizeAtom,
    minNotionalOrderAmountAtom,
    priceProtectionThresholdAtom,
    crossMaxAtom,
    effectiveIsolatedMaxAtom,
    ceilingsDifferAtom,
    maxOpenInterestAtom,
  };
});
tradingParameterBunja.debugLabel = 'tradingParameterBunja';
